import { Injectable } from '@angular/core';
import { Company, GetCompanyQueryResponse, ListCompaniesQueryResponse, MaintenanceType, UnlinkServiceProviderResponse, UpdateCompanyQueryResponse } from '@core/interfaces';
import { paginate } from '@core/utils/paginate';
import { Apollo } from 'apollo-angular';
import { sortBy } from 'lodash';
import { CompanyQueries } from './company.queries';

interface ListCompaniesResult {
  items: Company[];
  totalCount: number;
  filtered: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  constructor(
    private readonly apollo: Apollo,
    private readonly queries: CompanyQueries,
  ) { }

  async listCompanies({ where, options }): Promise<ListCompaniesResult> {
    const query = this.queries.listCompanies();
    const variables = {
      where,
      orderBy: options.orderBy,
      limit: options.limit,
      offset: options.offset,
    };

    return this.apollo.query<ListCompaniesQueryResponse>({
      query,
      variables
    })
    .toPromise()
    .then((response) => {
      return {
        items: response.data.companies.items,
        totalCount: response.data.companies.totalCount,
        filtered: Boolean(
          !!where.name_contains ||
          where.archived !== false ||
          !!where.field_account_id_contains
        )
      };
    });
  }

  async listAllMaintenanceTypes(): Promise<MaintenanceType[]> {
    const query = this.queries.listAllMaintenancesTypes();

    const maintenancesTypes = await paginate<MaintenanceType>({
      apollo: this.apollo,
      query,
      perPage: 100,
      itemsPropertyName: 'maintenanceTypes',
    });

    return sortBy(maintenancesTypes, (item) => item.archived ? 1 : 0);
  }

  async getCompanyById(id: string): Promise<Company> {
    const query = this.queries.getCompanyById();
    const variables = { id };
    return this.apollo
      .query<GetCompanyQueryResponse>({ query, variables})
      .toPromise()
      .then((response) => response.data.company);
  }

  async updateCompany(id: string, input: any): Promise<Company> {
    const query = this.queries.updateCompany();
    const variables = { id, input };
    return this.apollo
      .query<UpdateCompanyQueryResponse>({query, variables})
      .toPromise()
      .then((response) => response.data.updateCompany);
  }

  async archiveCompanyAndUnlinkServiceProvider(companyId: string): Promise<Company> {
    const query = this.queries.archiveCompanyAndUnlinkServiceProvider();
    const variables = { companyId };

    return this.apollo
      .query<UnlinkServiceProviderResponse>({ query, variables })
      .toPromise()
      .then((response) => response.data.archiveCompanyAndUnlinkServiceProvider);
  }
}
